import {
  TypeImplementation,
  withSystem,
  Plugins,
  EnvTypes,
} from '@packages/systems/wfdl';
import {
  functionOf,
  TText,
  TNumber,
  TListOfElement,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {qualify, typeName, iconName} from '@packages/systems/wfdl-Qualified';
import {type DynamoEnvValueType} from '../types';
import {TOption} from '@packages/systems/plugins/Option';
import {clamp} from '@packages/systems/core/utils/NumberUtils';
import {
  TPredicate,
  toBooleanTag,
} from '@packages/systems/plugins-utils/Boolean';
import {operand} from '@packages/systems/plugins/ConditionsOperand';
import {TDynamoNumber} from '@packages/systems/plugins-utils/DynamoGateway';

const TDecimalNumberSelector = TTypeConstructor(
  qualify(Plugins.name('Builtin'), typeName('DecimalNumberSelector'))
);

export const NumberType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Basic'), iconName('Number'))
  ),
});

// We need to clamp the precision before passing the value into `toFixed`
// because we may have expressions where the precision is set to greater
// than 8. Moreover, `toFixed` throws if called with number greater than 100.
function clampPrecisionArg(precision: number): number {
  return clamp(0, 8, precision);
}

const precisionType = withSystem({defaultValue: -1}, TDecimalNumberSelector);

// Common util between all the transformer functions for Dynamo Number
// that will make sure to correctly handle the available precision decimal
// values. Here a value of `-1` refers to the "any" precision. I.e. the
// precision is "trim all trailing zeros but show all significant digits"
const numberToPrecision = (decimals: unknown, num: number): string => {
  if (typeof decimals !== 'number' || decimals < 0) {
    return String(num);
  }

  return num.toFixed(clampPrecisionArg(Math.floor(decimals)));
};

export const dynamoNumberToText: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'Format'},
      functionOf(
        precisionType,
        functionOf(
          withSystem({isBindable: true}, TOption(TDynamoNumber)),
          TText
        )
      )
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value:
    (decimals: unknown) =>
    (num: DynamoEnvValueType<unknown>): string => {
      if (num === null || num.tag === 'None' || typeof num.val !== 'number') {
        return '';
      }
      return numberToPrecision(decimals, num.val);
    },
};

export const dynamoNumberToNumber: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'Format'},
      functionOf(
        precisionType,
        functionOf(
          withSystem({isBindable: true}, TOption(TDynamoNumber)),
          TNumber
        )
      )
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value:
    (decimals: unknown) =>
    (num: DynamoEnvValueType<unknown>): number => {
      if (num === null || num.tag === 'None' || typeof num.val !== 'number') {
        return 0;
      }

      return Number(numberToPrecision(decimals, num.val));
    },
};

export const dynamoNumberToListOfElements: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'Format'},
      functionOf(
        precisionType,
        functionOf(
          withSystem({isBindable: true}, TOption(TDynamoNumber)),
          TListOfElement
        )
      )
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (decimals: unknown) => (num: DynamoEnvValueType<unknown>) => {
    if (num === null || num.tag === 'None' || typeof num.val !== 'number') {
      return [];
    }

    const numString = numberToPrecision(decimals, num.val);

    return [
      {
        text: true,
        type: null,
        id: 'temp',
        children: [],
        data: {value: numString},
      },
    ];
  },
};

export const dynamoNumberEquals: EnvTypes.TypedValue = {
  envType: {
    exported: 'visible',
    dataType: withSystem(
      {label: 'is'},
      functionOf(
        withSystem({isBindable: true}, TNumber),
        TPredicate(TOption(TDynamoNumber))
      )
    ),
  },
  value: (num1: number) => (num2: DynamoEnvValueType<unknown>) => {
    if (num2 === null || num2.tag === 'None' || typeof num2.val !== 'number') {
      return toBooleanTag(false);
    }
    return toBooleanTag(num1 === num2.val);
  },
};

export const dynamoNumberDoesNotEqual: EnvTypes.TypedValue = {
  envType: {
    exported: 'visible',
    dataType: withSystem(
      {label: 'is not'},
      functionOf(
        withSystem({isBindable: true}, TNumber),
        TPredicate(TOption(TDynamoNumber))
      )
    ),
  },
  value: (num1: number) => (num2: DynamoEnvValueType<unknown>) => {
    if (num2 === null || num2.tag === 'None' || typeof num2.val !== 'number') {
      return toBooleanTag(true);
    }
    return toBooleanTag(num1 !== num2.val);
  },
};

export const dynamoNumberIsGreaterThan: EnvTypes.TypedValue = {
  envType: {
    exported: 'visible',
    dataType: withSystem(
      {label: 'is greater than'},
      functionOf(
        withSystem({isBindable: true}, TNumber),
        TPredicate(TOption(TDynamoNumber))
      )
    ),
  },
  value: (num1: number) => (num2: DynamoEnvValueType<unknown>) => {
    if (num2 === null || num2.tag === 'None' || typeof num2.val !== 'number') {
      return toBooleanTag(false);
    }
    return toBooleanTag(num2.val > num1);
  },
};

export const dynamoNumberIsGreaterThanEqualTo: EnvTypes.TypedValue = {
  envType: {
    exported: 'visible',
    dataType: withSystem(
      {label: 'is greater or equal'},
      functionOf(
        withSystem({isBindable: true}, TNumber),
        TPredicate(TOption(TDynamoNumber))
      )
    ),
  },
  value: (num1: number) => (num2: DynamoEnvValueType<unknown>) => {
    if (num2 === null || num2.tag === 'None' || typeof num2.val !== 'number') {
      return toBooleanTag(false);
    }
    return toBooleanTag(num2.val >= num1);
  },
};

export const dynamoNumberIsLessThan: EnvTypes.TypedValue = {
  envType: {
    exported: 'visible',
    dataType: withSystem(
      {label: 'is less than'},
      functionOf(
        withSystem({isBindable: true}, TNumber),
        TPredicate(TOption(TDynamoNumber))
      )
    ),
  },
  value: (num1: number) => (num2: DynamoEnvValueType<unknown>) => {
    if (num2 === null || num2.tag === 'None' || typeof num2.val !== 'number') {
      return toBooleanTag(false);
    }
    return toBooleanTag(num2.val < num1);
  },
};

export const dynamoNumberIsLessThanEqualTo: EnvTypes.TypedValue = {
  envType: {
    exported: 'visible',
    dataType: withSystem(
      {label: 'is less or equal'},
      functionOf(
        withSystem({isBindable: true}, TNumber),
        TPredicate(TOption(TDynamoNumber))
      )
    ),
  },
  value: (num1: number) => (num2: DynamoEnvValueType<unknown>) => {
    if (num2 === null || num2.tag === 'None' || typeof num2.val !== 'number') {
      return toBooleanTag(false);
    }
    return toBooleanTag(num2.val <= num1);
  },
};

export const dynamoNumberIsSet: EnvTypes.TypedValue = {
  envType: {
    exported: 'visible',
    dataType: withSystem({label: 'is set'}, TPredicate(TOption(TDynamoNumber))),
  },
  value: (num: DynamoEnvValueType<unknown>) => {
    if (num === null || num.tag === 'None' || typeof num.val !== 'number') {
      return toBooleanTag(false);
    }
    return toBooleanTag(true);
  },
};

export const dynamoNumberIsNotSet: EnvTypes.TypedValue = {
  envType: {
    exported: 'visible',
    dataType: withSystem(
      {label: 'is not set'},
      TPredicate(TOption(TDynamoNumber))
    ),
  },
  value: (num: DynamoEnvValueType<unknown>) => {
    if (num === null || num.tag === 'None' || typeof num.val !== 'number') {
      return toBooleanTag(true);
    }
    return toBooleanTag(false);
  },
};

export const dynamoNumberOperand = operand(TOption(TDynamoNumber));
